import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar.tsx";
import { Footer } from "@/components/layout/Footer.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { getPublishedStories, type PublishedStory } from "@/lib/storage/stories.ts";

export function PublicCatalogue() {
  const { t } = useTranslation();
  const [stories, setStories] = useState<PublishedStory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const published = await getPublishedStories();
      setStories(published);
      setLoading(false);
    };

    void load();
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-10 space-y-6">
        <div>
          <h1 className="font-pixel text-lg uppercase">{t("catalog.title")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("catalog.description")}
          </p>
        </div>

        {loading
          ? <p className="text-muted-foreground">{t("common.loading")}</p>
          : stories.length === 0
          ? <p className="text-muted-foreground">{t("catalog.empty")}</p>
          : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {stories.map((story) => (
                <Link key={story.id} to={`/catalog/${story.slug}`}>
                  <Card className="h-full border-2 hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
                    <CardHeader>
                      <CardTitle className="text-base leading-snug">{story.title}</CardTitle>
                      <CardDescription>
                        {new Date(story.publishedAt).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {story.objectiveExcerpt || t("catalog.noObjective")}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {story.theme && <Badge variant="secondary">{story.theme}</Badge>}
                        {story.language && <Badge variant="outline">{story.language}</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
      </main>
      <Footer />
    </div>
  );
}
